import type {
  BarcodeResult,
  DocumentResult,
  FieldResult,
  OcrBox,
  PageResult,
} from '../shared/desktop-api';
import { numericFieldDisplayEvidence } from './numeric-field-evidence';

export type FieldSource =
  | { kind: 'missing'; reason: string }
  | { kind: 'lookup'; attribution: string }
  | { kind: 'source'; page: PageResult; boxes: OcrBox[]; identity: string };
const missing = (reason = 'Source not located'): FieldSource => ({ kind: 'missing', reason });
const barcodeKey = (barcode: BarcodeResult) => [
  barcode.value,
  barcode.symbology,
  barcode.x,
  barcode.y,
  barcode.width,
  barcode.height,
];
const fieldKey = (field: FieldResult) =>
  JSON.stringify([
    field.fieldId,
    field.name,
    field.value,
    field.source,
    field.evidencePageNumber,
    field.evidenceLineIndexes,
    field.barcodeEvidence && barcodeKey(field.barcodeEvidence),
    field.lookupEvidence,
    field.markEvidence ?? null,
  ]);
const validBox = (box: OcrBox) =>
  [box.x, box.y, box.width, box.height].every(Number.isFinite) &&
  box.x >= 0 &&
  box.y >= 0 &&
  box.width > 0 &&
  box.height > 0;
/** The page's stored pixel size, or null when it was not recorded (0 or missing means unknown). */
export const knownPixelSize = (page: PageResult): { width: number; height: number } | null =>
  Number.isFinite(page.pixelWidth) &&
  Number.isFinite(page.pixelHeight) &&
  page.pixelWidth! > 0 &&
  page.pixelHeight! > 0
    ? { width: page.pixelWidth!, height: page.pixelHeight! }
    : null;

/**
 * Evidence selection only. Never search printed text for the field's value. `field` is the field as
 * the screen shows it: its source link repaired for display (numericFieldDisplayEvidence), as both
 * windows show it, so it is compared with the document's owner repaired the same way.
 */
export function resolveFieldSource(
  document: DocumentResult,
  field: FieldResult | undefined,
  revision: string,
): FieldSource {
  if (!field || !document.id || !revision) return missing();
  const owners = document.fields.filter((candidate) =>
    field.fieldId ? candidate.fieldId === field.fieldId : candidate.name === field.name,
  );
  if (
    owners.length !== 1 ||
    fieldKey(numericFieldDisplayEvidence(owners[0], document.pages)) !== fieldKey(field)
  )
    return missing('Source no longer matches this field');
  if (field.source === 'Configured default') return missing('Default value from job setup');
  if (field.lookupEvidence)
    return {
      kind: 'lookup',
      attribution: `${field.lookupEvidence.dataTableName} · ${field.lookupEvidence.explanation}`,
    };
  if (!Number.isInteger(field.evidencePageNumber) || field.evidencePageNumber < 1) return missing();
  const pages = document.pages.filter((page) => page.number === field.evidencePageNumber);
  if (pages.length !== 1) return missing('Source page is missing or ambiguous');
  const page = pages[0];
  if (!page.previewPath || page.notReadReason?.trim() || document.notReadReason?.trim())
    return missing('Source was not read');
  const boxes: OcrBox[] = [];
  if (field.barcodeEvidence) {
    const expected = JSON.stringify(barcodeKey(field.barcodeEvidence));
    if (
      page.barcodes.filter((barcode) => JSON.stringify(barcodeKey(barcode)) === expected).length !==
      1
    )
      return missing('Barcode source is missing or ambiguous');
    if (!validBox(field.barcodeEvidence)) return missing('Source area is invalid');
    boxes.push(field.barcodeEvidence);
  } else {
    const indexes = field.evidenceLineIndexes;
    if (
      !Array.isArray(indexes) ||
      !indexes.length ||
      indexes.length > 128 ||
      new Set(indexes).size !== indexes.length
    )
      return missing();
    for (const index of indexes) {
      if (!Number.isInteger(index) || index < 0 || index >= page.lines.length)
        return missing('Source lines are no longer available');
      const polygon = page.lines[index].polygon;
      if (
        !Array.isArray(polygon) ||
        polygon.length < 3 ||
        polygon.length > 32 ||
        polygon.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))
      )
        return missing('Source area is invalid');
      const area = polygon.reduce((sum, point, i) => {
        const next = polygon[(i + 1) % polygon.length];
        return sum + point.x * next.y - next.x * point.y;
      }, 0);
      if (!Number.isFinite(area) || area === 0) return missing('Source area is invalid');
      const x = Math.min(...polygon.map((point) => point.x)),
        y = Math.min(...polygon.map((point) => point.y));
      const box = {
        x,
        y,
        width: Math.max(...polygon.map((point) => point.x)) - x,
        height: Math.max(...polygon.map((point) => point.y)) - y,
      };
      if (!validBox(box)) return missing('Source area is invalid');
      boxes.push(box);
    }
    // A checkbox reading shows the box that was marked beside its caption.
    if (field.markEvidence && validBox(field.markEvidence)) boxes.push(field.markEvidence);
  }
  const identity = JSON.stringify([
    document.id,
    revision,
    fieldKey(field),
    page.number,
    page.previewPath,
    page.sourcePath,
    page.sourcePageNumber,
    page.sourceOccurrenceId,
    page.pixelWidth,
    page.pixelHeight,
    boxes,
  ]);
  const result: FieldSource = { kind: 'source', page, boxes, identity };
  const stored = knownPixelSize(page);
  if (stored && !fieldSourceCropBounds(result, stored.width, stored.height))
    return missing('Source area is outside the page');
  return result;
}

/** Bounds are checked against decoded ORIGINAL pixels before anything is shown. */
export function fieldSourceCropBounds(
  source: Extract<FieldSource, { kind: 'source' }>,
  width: number,
  height: number,
): OcrBox | null {
  const stored = knownPixelSize(source.page);
  if (
    ![width, height].every((value) => Number.isFinite(value) && value > 0) ||
    (stored && (stored.width !== width || stored.height !== height)) ||
    source.boxes.some(
      (box) => !validBox(box) || box.x + box.width > width || box.y + box.height > height,
    )
  )
    return null;
  const left = Math.min(...source.boxes.map((box) => box.x)),
    top = Math.min(...source.boxes.map((box) => box.y));
  const right = Math.max(...source.boxes.map((box) => box.x + box.width)),
    bottom = Math.max(...source.boxes.map((box) => box.y + box.height));
  // Keep the surrounding printed label/neighboring line, without inventing a location.
  const margin = Math.max(12, Math.min(48, (bottom - top) * 0.65));
  const x = Math.max(0, left - margin * 2),
    y = Math.max(0, top - margin);
  return {
    x,
    y,
    width: Math.min(width, right + margin * 2) - x,
    height: Math.min(height, bottom + margin) - y,
  };
}

export const sourcePageIdentity = (documentId: string, revision: string, page: PageResult) =>
  JSON.stringify([
    documentId,
    revision,
    page.number,
    page.previewPath,
    page.sourcePath,
    page.sourcePageNumber,
    page.sourceOccurrenceId,
    page.pixelWidth,
    page.pixelHeight,
  ]);
